/**
 * MyCopro - Validation Campaign Store
 * Gestion complète des campagnes de validation résidence
 *
 * Actions CS/Admin:
 *   - createCampaign: lance une nouvelle campagne
 *   - closeCampaign: clôture et délie les non-validés
 *   - sendReminder: (placeholder pour notification push)
 *
 * Actions Résident:
 *   - submitCode: soumet le mot de passe de validation
 *
 * Listeners:
 *   - subscribeToCampaigns: écoute en temps réel les campagnes actives
 *   - subscribeToResponses: écoute les réponses d'une campagne
 */

import { create } from 'zustand';
import {
  doc,
  collection,
  addDoc,
  getDoc,
  getDocFromServer,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  writeBatch,
  Unsubscribe,
  Timestamp
} from 'firebase/firestore';
import { db } from '@/config/firebase';
import type {
  ValidationCampaign,
  CampaignResponse,
  CreateCampaignData,
  CampaignSummary,
  CampaignStatus
} from '@/types/validationCampaign';
import { logger } from '@/utils/logger';
import {
  hashCampaignCode,
  verifyCampaignCode,
  getCampaignDaysRemaining
} from '@/types/validationCampaign';

// ============================================================================
// TYPES DU STORE
// ============================================================================

interface ValidationCampaignState {
  // Campagnes
  campaigns: ValidationCampaign[];
  activeCampaign: ValidationCampaign | null;

  // Réponses de la campagne active
  responses: CampaignResponse[];

  // Statut du résident courant pour la campagne active
  currentUserResponse: CampaignResponse | null;

  // État
  isLoading: boolean;
  error: string | null;

  // Subscriptions
  _unsubCampaigns: Unsubscribe | null;
  _unsubResponses: Unsubscribe | null;
  _unsubUserResponse: Unsubscribe | null;

  // Jeton incrémenté à chaque appel de loadCurrentUserResponse : permet d'ignorer
  // les résultats d'un appel précédent qui résoudrait après un appel plus récent
  // (ex: activeCampaign change rapidement au chargement, cache stale → serveur).
  _userResponseRequestId: number;
}

interface ValidationCampaignActions {
  // ─── CS/Admin ───────────────────────────────────────────────────────
  createCampaign: (
    residenceId: string,
    data: CreateCampaignData,
    createdBy: string,
    createdByName: string
  ) => Promise<string | null>;

  closeCampaign: (
    residenceId: string,
    campaignId: string,
    closedBy: string
  ) => Promise<{ validatedCount: number; unlinkedCount: number } | null>;

  resetAttempts: (
    residenceId: string,
    campaignId: string,
    targetUserId: string
  ) => Promise<boolean>;

  // Valide manuellement un résident pour la campagne en cours, comme s'il
  // avait saisi le bon mot de passe (ex: validation déjà faite à l'oral).
  manualValidateMember: (
    targetUserId: string,
    validatedBy: string
  ) => Promise<boolean>;

  sendReminderNotification: (
    residenceId: string,
    campaignId: string,
    pendingMemberIds: string[],
    senderId: string,
    senderName: string
  ) => Promise<number>;

  // ─── Résident ───────────────────────────────────────────────────────
  submitCode: (
    residenceId: string,
    campaignId: string,
    userId: string,
    userName: string,
    userCategory: string,
    code: string
  ) => Promise<{ success: boolean; attemptsLeft: number; error?: string }>;

  // ─── Listeners ──────────────────────────────────────────────────────
  subscribeToCampaigns: (residenceId: string) => void;
  subscribeToResponses: (residenceId: string, campaignId: string) => void;
  unsubscribeAll: () => void;

  // ─── Helpers ────────────────────────────────────────────────────────
  loadCurrentUserResponse: (
    residenceId: string,
    campaignId: string,
    userId: string
  ) => Promise<void>;

  getCampaignSummary: () => CampaignSummary | null;

  // ─── Reset ──────────────────────────────────────────────────────────
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

type ValidationCampaignStore = ValidationCampaignState & ValidationCampaignActions;

// ============================================================================
// ÉTAT INITIAL
// ============================================================================

const initialState: ValidationCampaignState = {
  campaigns: [],
  activeCampaign: null,
  responses: [],
  currentUserResponse: null,
  isLoading: false,
  error: null,
  _unsubCampaigns: null,
  _unsubResponses: null,
  _unsubUserResponse: null,
  _userResponseRequestId: 0
};

// ============================================================================
// STORE
// ============================================================================

export const useValidationCampaignStore = create<ValidationCampaignStore>((set, get) => ({
  ...initialState,

  // ══════════════════════════════════════════════════════════════════════
  // CRÉATION DE CAMPAGNE (CS/Admin uniquement)
  // ══════════════════════════════════════════════════════════════════════

  createCampaign: async (residenceId, data, createdBy, createdByName) => {
    set({ isLoading: true, error: null });

    try {
      // Vérifier qu'il n'y a pas déjà une campagne active
      const { activeCampaign } = get();
      if (activeCampaign) {
        set({ isLoading: false, error: 'Une campagne est déjà en cours' });
        return null;
      }

      // Hasher le code
      const codeHash = await hashCampaignCode(data.code);
      const now = new Date().toISOString();

      const campaignData = {
        residenceId,
        codeHash,
        code: data.code.trim().toUpperCase(),
        startDate: data.startDate,
        endDate: data.endDate,
        status: 'active' as CampaignStatus,
        maxAttempts: data.maxAttempts || 5,
        createdBy,
        createdByName,
        createdAt: now
      };

      const campaignsRef = collection(db, 'residences', residenceId, 'validationCampaigns');
      const docRef = await addDoc(campaignsRef, campaignData);

      set({ isLoading: false });
      return docRef.id;
    } catch (error) {
      console.error('[ValidationCampaignStore] Error creating campaign:', error);
      set({ isLoading: false, error: 'Erreur lors de la création de la campagne' });
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // CLÔTURE DE CAMPAGNE (CS/Admin uniquement)
  // Délie les membres qui n'ont pas validé
  // ══════════════════════════════════════════════════════════════════════

  closeCampaign: async (residenceId, campaignId, closedBy) => {
    set({ isLoading: true, error: null });

    try {
      // 1. Récupérer la campagne (dates de début/fin) et tous les membres actifs de la résidence
      const campaignRef = doc(
        db, 'residences', residenceId, 'validationCampaigns', campaignId
      );
      const campaignSnap = await getDoc(campaignRef);
      const campaignData = campaignSnap.data();
      const campaignStart = campaignData ? new Date(campaignData.startDate) : null;
      const campaignEnd = campaignData ? new Date(campaignData.endDate) : null;

      const membersQuery = query(
        collection(db, 'members'),
        where('residenceId', '==', residenceId),
        where('status', '==', 'active')
      );
      const membersSnap = await getDocs(membersQuery);

      // Exclure admin et syndic (ils ne sont pas soumis à la validation)
      const eligibleMembers = membersSnap.docs.filter(d => {
        const cat = d.data().category;
        return !['admin', 'syndic'].includes(cat);
      });

      // 2. Récupérer toutes les réponses
      const responsesRef = collection(
        db, 'residences', residenceId, 'validationCampaigns', campaignId, 'responses'
      );
      const responsesSnap = await getDocs(responsesRef);
      const validatedUserIds = new Set<string>();
      responsesSnap.docs.forEach(d => {
        if (d.data().validated === true) {
          validatedUserIds.add(d.id);
        }
      });

      // Membres validés manuellement par le CS pendant la campagne (nouvel arrivant
      // approuvé par le CS pendant la période, ou validation manuelle via le suivi de
      // campagne) — comptent comme validés et ne sont pas déliés. L'auto-validation
      // (sans validatedBy) ne compte pas.
      const isLateValidatedByCS = (memberData: any) => {
        if (!memberData.validatedBy || !memberData.validatedAt || !campaignStart || !campaignEnd) {
          return false;
        }
        const validatedAt = memberData.validatedAt.toDate
          ? memberData.validatedAt.toDate()
          : new Date(memberData.validatedAt);
        return validatedAt >= campaignStart && validatedAt <= campaignEnd;
      };

      // 3. Identifier les non-validés
      const toUnlink = eligibleMembers.filter(m =>
        !validatedUserIds.has(m.id) && !isLateValidatedByCS(m.data())
      );
      const lateValidatedCount = eligibleMembers.filter(m =>
        !validatedUserIds.has(m.id) && isLateValidatedByCS(m.data())
      ).length;
      const validatedCount = validatedUserIds.size + lateValidatedCount;

      // 4. Batch: délier les non-validés + clôturer la campagne
      const batch = writeBatch(db);

      // Délier chaque membre non-validé
      for (const memberDoc of toUnlink) {
        const memberRef = doc(db, 'members', memberDoc.id);
        batch.update(memberRef, {
          residenceId: null,
          status: 'pending',
          updatedAt: new Date().toISOString()
        });
      }

      // Clôturer la campagne
      batch.update(campaignRef, {
        status: 'closed',
        closedAt: new Date().toISOString(),
        closedBy,
        totalMembers: eligibleMembers.length,
        validatedCount,
        unlinkedCount: toUnlink.length
      });

      await batch.commit();

      const result = {
        validatedCount,
        unlinkedCount: toUnlink.length
      };

      set({ isLoading: false });
      return result;
    } catch (error) {
      console.error('[ValidationCampaignStore] Error closing campaign:', error);
      set({ isLoading: false, error: 'Erreur lors de la clôture de la campagne' });
      return null;
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // RÉINITIALISER LES TENTATIVES D'UN RÉSIDENT (CS/Admin)
  // Permet de redonner des tentatives à un résident qui les a épuisées
  // ══════════════════════════════════════════════════════════════════════

  resetAttempts: async (residenceId, campaignId, targetUserId) => {
    try {
      const responseRef = doc(
        db, 'residences', residenceId, 'validationCampaigns', campaignId, 'responses', targetUserId
      );
      const snap = await getDoc(responseRef);

      if (snap.exists()) {
        // Remettre les tentatives à 0, garder validated à false
        await updateDoc(responseRef, {
          attempts: 0,
          validated: false,
          lastAttemptAt: null
        });
      }
      // Si pas de réponse existante, rien à réinitialiser

      return true;
    } catch (error) {
      console.error('[ValidationCampaignStore] Error resetting attempts:', error);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // VALIDATION MANUELLE D'UN RÉSIDENT (CS/Admin)
  // Marque le résident comme recensé sans qu'il ait saisi le mot de passe
  // (ex: validation faite de vive voix). Réutilise validatedAt/validatedBy
  // sur le document membre, déjà exploités par useValidationCampaign pour
  // exempter les résidents validés par le CS pendant la campagne.
  // ══════════════════════════════════════════════════════════════════════

  manualValidateMember: async (targetUserId, validatedBy) => {
    try {
      const memberRef = doc(db, 'members', targetUserId);
      await updateDoc(memberRef, {
        validatedAt: Timestamp.now(),
        validatedBy,
        updatedAt: new Date().toISOString()
      });
      return true;
    } catch (error) {
      console.error('[ValidationCampaignStore] Error manually validating member:', error);
      return false;
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // ENVOYER UN RAPPEL PUSH AUX RÉSIDENTS EN ATTENTE (CS/Admin)
  // Crée une notification Firestore par résident en attente.
  // La Cloud Function onNotificationCreated se déclenche sur chaque
  // document créé et envoie le push FCM via targetUserId.
  // On utilise addDoc (pas writeBatch) pour garantir que chaque
  // création déclenche bien la CF indépendamment.
  // ══════════════════════════════════════════════════════════════════════

  sendReminderNotification: async (residenceId, campaignId, pendingMemberIds, senderId, senderName) => {
    try {
      const now = new Date().toISOString();
      const today = now.split('T')[0];
      let count = 0;

      const notificationsRef = collection(db, 'residences', residenceId, 'notifications');

      for (const memberId of pendingMemberIds) {
        try {
          await addDoc(notificationsRef, {
            // ── Champs standard Notification (in-app) ──
            residenceId,
            type: 'general',
            title: 'Rappel : validation résidence',
            message: 'Pensez à valider votre résidence en saisissant le mot de passe. Consultez l\'affichage dans le hall ou votre boîte aux lettres.',
            read: false,
            restrictedTo: null,
            date: today,
            createdAt: now,

            // ── Champs Cloud Function (push FCM) ──
            targetUserId: memberId,
            sentBy: senderId,
            sentByName: senderName,

            // ── Navigation ──
            linkTo: '/',                  // In-app: redirige vers le dashboard
            link: '/'                     // CF: champ lu pour le payload FCM data
          });
          count++;
          logger.db(` Notification créée pour ${memberId}`);
        } catch (innerError) {
          console.error(`[ValidationCampaignStore] Erreur notification pour ${memberId}:`, innerError);
        }
      }

      logger.db(` ${count}/${pendingMemberIds.length} notifications créées`);
      return count;
    } catch (error) {
      console.error('[ValidationCampaignStore] Error sending reminders:', error);
      return 0;
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // SOUMISSION DE CODE (Résident)
  // ══════════════════════════════════════════════════════════════════════

  submitCode: async (residenceId, campaignId, userId, userName, userCategory, code) => {
    set({ isLoading: true, error: null });

    try {
      // 1. Récupérer la campagne pour le hash et maxAttempts
      const campaignRef = doc(
        db, 'residences', residenceId, 'validationCampaigns', campaignId
      );
      const campaignSnap = await getDoc(campaignRef);

      if (!campaignSnap.exists()) {
        set({ isLoading: false });
        return { success: false, attemptsLeft: 0, error: 'Campagne introuvable' };
      }

      const campaign = campaignSnap.data();
      const maxAttempts = campaign.maxAttempts || 5;

      // 2. Récupérer la réponse existante (si déjà tenté)
      const responseRef = doc(
        db, 'residences', residenceId, 'validationCampaigns', campaignId, 'responses', userId
      );
      const responseSnap = await getDoc(responseRef);
      const existing = responseSnap.exists() ? responseSnap.data() : null;

      // Déjà validé ?
      if (existing?.validated) {
        set({ isLoading: false });
        return { success: true, attemptsLeft: maxAttempts - (existing.attempts || 0) };
      }

      // Nombre de tentatives épuisé ?
      const currentAttempts = existing?.attempts || 0;
      if (currentAttempts >= maxAttempts) {
        set({ isLoading: false });
        return { success: false, attemptsLeft: 0, error: 'Nombre de tentatives épuisé' };
      }

      // 3. Vérifier le code
      const isValid = await verifyCampaignCode(code, campaign.codeHash);
      const newAttempts = currentAttempts + 1;
      const now = new Date().toISOString();

      const responseData: Record<string, any> = {
        userId,
        userName,
        userCategory,
        validated: isValid,
        attempts: newAttempts,
        lastAttemptAt: now
      };

      if (isValid) {
        responseData.validatedAt = now;
      }

      await setDoc(responseRef, responseData, { merge: true });

      // Mettre à jour le state local
      set({
        isLoading: false,
        currentUserResponse: {
          id: userId,
          ...responseData
        } as CampaignResponse
      });

      return {
        success: isValid,
        attemptsLeft: maxAttempts - newAttempts,
        error: isValid ? undefined : 'Mot de passe incorrect'
      };
    } catch (error) {
      console.error('[ValidationCampaignStore] Error submitting code:', error);
      set({ isLoading: false, error: 'Erreur lors de la validation' });
      return { success: false, attemptsLeft: 0, error: 'Erreur technique' };
    }
  },

  // ══════════════════════════════════════════════════════════════════════
  // LISTENERS TEMPS RÉEL
  // ══════════════════════════════════════════════════════════════════════

  subscribeToCampaigns: (residenceId) => {
    const { _unsubCampaigns } = get();
    if (_unsubCampaigns) _unsubCampaigns();

    const campaignsRef = collection(db, 'residences', residenceId, 'validationCampaigns');
    const q = query(campaignsRef, orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, (snapshot) => {
      const campaigns: ValidationCampaign[] = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as ValidationCampaign));

      const activeCampaign = campaigns.find(c => c.status === 'active') || null;

      set({ campaigns, activeCampaign });
    }, (error) => {
      console.error('[ValidationCampaignStore] Campaigns listener error:', error);
    });

    set({ _unsubCampaigns: unsub });
  },

  subscribeToResponses: (residenceId, campaignId) => {
    const { _unsubResponses } = get();
    if (_unsubResponses) _unsubResponses();

    const responsesRef = collection(
      db, 'residences', residenceId, 'validationCampaigns', campaignId, 'responses'
    );

    const unsub = onSnapshot(responsesRef, (snapshot) => {
      const responses: CampaignResponse[] = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      } as CampaignResponse));

      set({ responses });
    }, (error) => {
      console.error('[ValidationCampaignStore] Responses listener error:', error);
    });

    set({ _unsubResponses: unsub });
  },

  unsubscribeAll: () => {
    const { _unsubCampaigns, _unsubResponses, _unsubUserResponse, _userResponseRequestId } = get();
    if (_unsubCampaigns) _unsubCampaigns();
    if (_unsubResponses) _unsubResponses();
    if (_unsubUserResponse) _unsubUserResponse();
    set({
      _unsubCampaigns: null,
      _unsubResponses: null,
      _unsubUserResponse: null,
      // Invalide tout appel loadCurrentUserResponse en cours pour éviter qu'il
      // ne (re)crée un listener après ce nettoyage (fuite + écrasement du state).
      _userResponseRequestId: _userResponseRequestId + 1
    });
  },

  // ══════════════════════════════════════════════════════════════════════
  // HELPERS
  // ══════════════════════════════════════════════════════════════════════

  loadCurrentUserResponse: async (residenceId, campaignId, userId) => {
    const { _unsubUserResponse } = get();
    if (_unsubUserResponse) _unsubUserResponse();

    // Jeton unique à cet appel : si activeCampaign change rapidement au chargement
    // (ex: cache local renvoie d'abord une ancienne campagne avant correction serveur),
    // plusieurs appels se chevauchent. Sans ce garde, la résolution tardive d'un appel
    // obsolète (getDoc/getDocFromServer d'une campagne précédente) écrase le résultat
    // frais du dernier appel — la carte affiche alors "à valider" malgré validated:true.
    const requestId = get()._userResponseRequestId + 1;
    set({ _userResponseRequestId: requestId });
    const isStale = () => get()._userResponseRequestId !== requestId;

    try {
      const responseRef = doc(
        db, 'residences', residenceId, 'validationCampaigns', campaignId, 'responses', userId
      );

      // Lire le cache local IMMÉDIATEMENT pour éviter le flash "orange" au chargement.
      // Si l'utilisateur a déjà confirmé, le cache contient validated:true et la carte
      // s'affiche verte dès le premier rendu, sans attendre le réseau.
      const cachedSnap = await getDoc(responseRef);
      if (isStale()) return;
      if (cachedSnap.exists()) {
        set({ currentUserResponse: { id: cachedSnap.id, ...cachedSnap.data() } as CampaignResponse });
      }

      // Confirmer ensuite avec le serveur pour corriger un éventuel cache stale.
      try {
        const serverSnap = await getDocFromServer(responseRef);
        if (isStale()) return;
        set({
          currentUserResponse: serverSnap.exists()
            ? ({ id: serverSnap.id, ...serverSnap.data() } as CampaignResponse)
            : null
        });
      } catch {
        // Hors-ligne : on laisse l'onSnapshot gérer via le cache local
      }
      if (isStale()) return;

      // Listener temps réel pour détecter un reset de tentatives par le CS.
      // includeMetadataChanges: true pour pouvoir ignorer les tirs depuis le cache stale
      // (qui sinon écraseraient le résultat frais de getDocFromServer ci-dessus).
      const unsub = onSnapshot(responseRef, { includeMetadataChanges: true }, (snap) => {
        if (isStale()) { unsub(); return; }
        // Ignorer les résultats venant uniquement du cache local (pas d'écriture locale en attente)
        if (snap.metadata.fromCache && !snap.metadata.hasPendingWrites) return;
        if (snap.exists()) {
          set({ currentUserResponse: { id: snap.id, ...snap.data() } as CampaignResponse });
        } else {
          set({ currentUserResponse: null });
        }
      }, (error) => {
        console.error('[ValidationCampaignStore] User response listener error:', error);
      });

      if (isStale()) { unsub(); return; }
      set({ _unsubUserResponse: unsub });
    } catch (error) {
      console.error('[ValidationCampaignStore] Error subscribing to user response:', error);
    }
  },

  getCampaignSummary: () => {
    const { activeCampaign, responses } = get();
    if (!activeCampaign) return null;

    const validatedCount = responses.filter(r => r.validated).length;
    const failedCount = responses.filter(r => !r.validated && r.attempts >= (activeCampaign.maxAttempts || 5)).length;
    const pendingCount = (activeCampaign.totalMembers || 0) - validatedCount - failedCount;
    const totalMembers = activeCampaign.totalMembers || 0;
    const daysRemaining = getCampaignDaysRemaining(activeCampaign.endDate);
    const progressPercent = totalMembers > 0
      ? Math.round((validatedCount / totalMembers) * 100)
      : 0;

    return {
      campaign: activeCampaign,
      responses,
      validatedCount,
      pendingCount,
      failedCount,
      totalMembers,
      daysRemaining,
      progressPercent
    };
  },

  // ══════════════════════════════════════════════════════════════════════
  // RESET
  // ══════════════════════════════════════════════════════════════════════

  setLoading: (isLoading) => set({ isLoading }),
  setError: (error) => set({ error }),

  reset: () => {
    const { _unsubCampaigns, _unsubResponses, _unsubUserResponse } = get();
    if (_unsubCampaigns) _unsubCampaigns();
    if (_unsubResponses) _unsubResponses();
    if (_unsubUserResponse) _unsubUserResponse();
    set(initialState);
  }
}));

// ============================================================================
// SÉLECTEURS
// ============================================================================

export const selectActiveCampaign = (state: ValidationCampaignStore) => state.activeCampaign;
export const selectCampaigns = (state: ValidationCampaignStore) => state.campaigns;
export const selectResponses = (state: ValidationCampaignStore) => state.responses;
export const selectCurrentUserResponse = (state: ValidationCampaignStore) => state.currentUserResponse;
export const selectIsLoading = (state: ValidationCampaignStore) => state.isLoading;

export default useValidationCampaignStore;
